import React, { useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
// components
import { Loader } from '@progress/kendo-react-indicators';
import BaseInput from 'shared/inputs/BaseInput';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label, Error } from '@progress/kendo-react-labels';
// fetch
import { getCityStateFromPostalCode } from 'src/fetch/general';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';
import { emptyObject } from 'src/shared/helpers/utils';

/**
 * A postal code input form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseInput>} BaseInputProps
 *
 * @param {BaseFieldRenderProps & BaseInputProps} fieldRenderProps
 * @returns {React.ReactElement} Postal code form field.
 */
const FormPostalCodeInput = (fieldRenderProps) => {
  const {
    validationMessage,
    visited,
    label,
    id,
    valid,
    disabled,
    type,
    optional,
    onChange,
    onGeolocationFound,
    readOnly,
    Input = BaseInput,
    InputProps = emptyObject,
    ...others
  } = fieldRenderProps;

  const [isLoading, setIsLoading] = useState(false);

  const getGeolocation = useCallback(
    debounce((postalCode) => {
      setIsLoading(true);
      getCityStateFromPostalCode(postalCode)
        .then((data) => {
          onGeolocationFound(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500),
    [],
  );

  const handleChange = (e) => {
    onChange(e);
    const {
      target: { value },
    } = e;
    if (value && value.length === 5 && onGeolocationFound) {
      getGeolocation(value);
    }
  };

  const showValidationMessage = visited && validationMessage;
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <FieldWrapper>
      {label && (
        <Label
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
          {...(readOnly ? { className: 'label-readonly' } : {})}
        >
          {label}
        </Label>
      )}
      <div className='k-form-field-wrap'>
        <Input
          valid={valid}
          type={type}
          id={id}
          disabled={disabled}
          ariaDescribedBy={errorId}
          readOnly={readOnly}
          onChange={handleChange}
          {...others}
          {...InputProps}
        />
        {isLoading && <Loader size='small' type='pulsing' />}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  );
};

export default FormPostalCodeInput;
